import type { LeadStatus } from "../../convex/schemas/validators/enums";
import type { PaymentTerms, QuoteStatus } from "../../convex/schemas/validators/quoteEnums";
import type {
  OrderPaymentStatus,
  OrderStage,
} from "../../convex/constants/statuses";
import {
  ACTION_OWNER,
  STAGE,
  type ActionOwner,
  type Stage,
} from "../../convex/constants/stage";
import {
  buyerHoldsPayment,
  buyerWasCharged,
  isBuyerFullyRefunded,
} from "../orders/buyer-payment";
import { isBankPaymentClearing, isHeldForBankPayment } from "../orders/bank-clearing";
import { isOnCreditTerms } from "../orders/credit-terms";
import { awaitsAccountingDecision } from "../orders/accounting-review";

export interface ResolvedStage {
  stage: Stage;
  actionOwner: ActionOwner;
}

export interface LeadStageInput {
  status: LeadStatus;
}

export interface QuoteStageInput {
  status: QuoteStatus;
  quoteSource?: "buyer" | "vendor" | "admin" | "vendor_direct";
  expiresAt?: number | null;
  now?: number;
}

export interface OrderStageInput {
  stage: OrderStage;
  paymentStatus: OrderPaymentStatus;
  scheduling?: {
    pickupDate?: number;
    pickupAddressId?: string;
    pickupProposal?: {
      status: "proposed" | "accepted" | "rejected" | "cancelled";
      pickupDate?: number;
      pickupAddressId?: string;
    };
  };
  pricing?: { buyerGrandTotalCents: number };
  pickupCount?: { status: string; closedAt?: number };
  accountingReview?: { status: string };
  payment?: {
    checkoutReview?: { status: string };
    refundAmount?: number;
    isFullRefund?: boolean;
    netDays?: number;
    termsDays?: number;
    paymentTerms?: PaymentTerms;
    terms?: PaymentTerms;
    paidAt?: number;
    invoiceStatus?: string;
    stripeInvoiceId?: string;
    collection?: string;
    bankClearing?: { status: string };
  };
}

export function resolveLeadStage(lead: LeadStageInput): ResolvedStage {
  switch (lead.status) {
    case "new":
    case "confirmed":
    case "follow_up":
      return { stage: STAGE.SUBMITTED, actionOwner: ACTION_OWNER.ADMIN };
    case "contacted":
    case "quoted":
      return {
        stage: STAGE.VENDOR_RESPONDING,
        actionOwner: ACTION_OWNER.VENDOR,
      };
    case "won":
      return { stage: STAGE.COMPLETE, actionOwner: ACTION_OWNER.SYSTEM };
    case "lost":
      return { stage: STAGE.CANCELLED, actionOwner: ACTION_OWNER.SYSTEM };
  }
  return { stage: STAGE.SUBMITTED, actionOwner: ACTION_OWNER.ADMIN };
}

export function resolveQuoteStage(quote: QuoteStageInput): ResolvedStage {
  if (isExpiredOpenQuote(quote)) {
    return { stage: STAGE.EXPIRED, actionOwner: ACTION_OWNER.SYSTEM };
  }
  switch (quote.status) {
    case "pending_review":
      return { stage: STAGE.DRAFT, actionOwner: ACTION_OWNER.VENDOR };
    case "draft":
      return {
        stage: STAGE.DRAFT,
        actionOwner:
          quote.quoteSource === "admin"
            ? ACTION_OWNER.ADMIN
            : ACTION_OWNER.VENDOR,
      };
    case "sent":
    case "viewed":
      return {
        stage: STAGE.AWAITING_REVIEW,
        actionOwner:
          quote.quoteSource === "buyer"
            ? ACTION_OWNER.VENDOR
            : ACTION_OWNER.BUYER,
      };
    case "accepted":
    case "converted":
    case "paid":
      return {
        stage: STAGE.CONFIRMING_PICKUP,
        actionOwner: ACTION_OWNER.SYSTEM,
      };
    case "rejected":
    case "cancelled":
      return { stage: STAGE.CANCELLED, actionOwner: ACTION_OWNER.SYSTEM };
    case "expired":
    case "no_response":
      return { stage: STAGE.EXPIRED, actionOwner: ACTION_OWNER.SYSTEM };
  }
  return { stage: STAGE.AWAITING_REVIEW, actionOwner: ACTION_OWNER.SYSTEM };
}

function isExpiredOpenQuote(quote: QuoteStageInput): boolean {
  return (
    (quote.status === "sent" || quote.status === "viewed") &&
    quote.expiresAt !== undefined &&
    quote.expiresAt !== null &&
    quote.expiresAt < (quote.now ?? Date.now())
  );
}

function resolveDraftStage(order: OrderStageInput): ResolvedStage {
  if (!isHeldForBankPayment(order.payment)) {
    return { stage: STAGE.DRAFT, actionOwner: ACTION_OWNER.ADMIN };
  }
  return {
    stage: STAGE.CONFIRMED_AWAITING_PAYMENT,
    actionOwner: isBankPaymentClearing(order.payment) ? ACTION_OWNER.SYSTEM : ACTION_OWNER.BUYER,
  };
}

function collectsOnTerms(order: OrderStageInput): boolean {
  return order.payment?.collection === "terms" || isOnCreditTerms(order.payment);
}

export function resolveOrderStage(order: OrderStageInput): ResolvedStage {
  if (isBuyerFullyRefunded(order)) {
    return { stage: STAGE.REFUNDED, actionOwner: ACTION_OWNER.SYSTEM };
  }
  switch (order.stage) {
    case "draft":
      return resolveDraftStage(order);
    case "pending_approval":
      return {
        stage:
          buyerHoldsPayment(order) || collectsOnTerms(order)
            ? STAGE.AWAITING_REVIEW
            : STAGE.CONFIRMED_AWAITING_PAYMENT,
        actionOwner: ACTION_OWNER.ADMIN,
      };
    case "awaiting_pickup":
      return resolveAwaitingPickupStage(order);
    case "in_transit":
      return { stage: STAGE.IN_TRANSIT, actionOwner: ACTION_OWNER.SYSTEM };
    case "delivered":
      return {
        stage: STAGE.DELIVERED,
        actionOwner: deliveredBalanceOpen(order)
          ? ACTION_OWNER.BUYER
          : ACTION_OWNER.ADMIN,
      };
    case "completed":
      return { stage: STAGE.COMPLETE, actionOwner: ACTION_OWNER.SYSTEM };
    case "cancelled":
      return { stage: STAGE.CANCELLED, actionOwner: ACTION_OWNER.SYSTEM };
    case "refunded":
      return { stage: STAGE.REFUNDED, actionOwner: ACTION_OWNER.SYSTEM };
  }
  return assertUnreachableStage(order.stage);
}

const UNSETTLED_PAYMENT_STATUSES: ReadonlySet<string> = new Set([
  "unpaid",
  "failed",
  "invoiced",
]);

function assertUnreachableStage(stage: never): never {
  throw new Error(
    `Unmapped order stage in resolveOrderStage: ${String(stage)}`,
  );
}

function deliveredBalanceOpen(order: OrderStageInput): boolean {
  return (
    UNSETTLED_PAYMENT_STATUSES.has(order.paymentStatus) &&
    !buyerWasCharged(order) &&
    order.pricing?.buyerGrandTotalCents !== 0
  );
}

export function acceptedDateStands(
  order: Pick<OrderStageInput, "scheduling">,
): boolean {
  const scheduling = order.scheduling;
  const proposal = scheduling?.pickupProposal;
  const live = scheduling?.pickupDate;
  if (live === undefined) return false;
  const dateStands =
    proposal?.pickupDate === undefined || proposal.pickupDate === live;
  const addressStands =
    proposal?.pickupAddressId === undefined ||
    scheduling?.pickupAddressId === undefined ||
    proposal.pickupAddressId === scheduling.pickupAddressId;
  return dateStands && addressStands;
}

export function legacyCommittedSchedule(
  order: Pick<OrderStageInput, "scheduling">,
): boolean {
  return (
    order.scheduling?.pickupProposal === undefined &&
    order.scheduling?.pickupDate !== undefined
  );
}

export function pendingPickupProposal<Proposal extends { status: string }>(
  proposal: Proposal | undefined,
): Proposal | undefined {
  return proposal?.status === "proposed" ? proposal : undefined;
}

export function pickupScheduleStands(
  order: Pick<OrderStageInput, "scheduling">,
): boolean {
  return (
    legacyCommittedSchedule(order) ||
    (order.scheduling?.pickupProposal?.status === "accepted" &&
      acceptedDateStands(order))
  );
}

function countStillOpen(order: OrderStageInput): boolean {
  const count = order.pickupCount;
  return (
    count !== undefined &&
    count.closedAt === undefined &&
    (count.status === "awaiting_buyer" || count.status === "reported")
  );
}

function owesPayment(order: OrderStageInput): boolean {
  if (countStillOpen(order)) return false;
  if (isOnCreditTerms(order.payment) && order.paymentStatus !== "failed") {
    return false;
  }
  return (
    UNSETTLED_PAYMENT_STATUSES.has(order.paymentStatus) &&
    !buyerWasCharged(order) &&
    order.pricing?.buyerGrandTotalCents !== 0
  );
}

function resolveAwaitingPickupStage(order: OrderStageInput): ResolvedStage {
  const pickupStatus = order.scheduling?.pickupProposal?.status;
  if (pickupStatus === "proposed") {
    return { stage: STAGE.CONFIRMING_PICKUP, actionOwner: ACTION_OWNER.BUYER };
  }
  if (pickupStatus === "rejected") {
    return { stage: STAGE.CONFIRMING_PICKUP, actionOwner: ACTION_OWNER.VENDOR };
  }
  if (
    awaitsAccountingDecision(order) ||
    order.paymentStatus === "awaiting_approval" ||
    order.paymentStatus === "unknown"
  ) {
    return {
      stage: STAGE.CONFIRMED_AWAITING_PAYMENT,
      actionOwner: ACTION_OWNER.ADMIN,
    };
  }
  if (owesPayment(order)) {
    return {
      stage: STAGE.CONFIRMED_AWAITING_PAYMENT,
      actionOwner: ACTION_OWNER.BUYER,
    };
  }
  if (pickupScheduleStands(order)) {
    return { stage: STAGE.SCHEDULED, actionOwner: ACTION_OWNER.VENDOR };
  }
  return {
    stage: STAGE.AWAITING_SCHEDULING,
    actionOwner: ACTION_OWNER.VENDOR,
  };
}
